"use client";

import React from "react";
import { Wrench, TestTubes, Scale, SlidersHorizontal as Sliders, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const applicationAreas = [{
  id: "maintenance",
  title: "Routine Instrument Maintenance",
  tag: "Preventive Care",
  description: "Scheduled servicing, optical alignment, detector health checks, and preventive component replacement to ensure uninterrupted laboratory uptime.",
  points: ["Scheduled lifecycle audits", "Optical and mechanical checks", "Emergency response protocols"],
  iconName: "wrench",
  linkPath: "/PreventiveMaintenanceAndRepairsPage"
}, {
  id: "sample-handling",
  title: "Sample Handling & Conditioning",
  tag: "Preparation",
  description: "Robust workflows and automated sample presentation interfaces for complex solids, powders, liquids, and slurry matrices.",
  points: ["Consistent sample presentation", "Temperature & moisture conditioning", "Minimised cross-contamination"],
  iconName: "tubes",
  linkPath: "/PowderTestingPage"
}, {
  id: "calibration",
  title: "Calibration & Verification",
  tag: "Compliance",
  description: "Certified reference standards, wavelength calibration, photometrics, and comprehensive validation protocols compliant with pharmacopeial standards.",
  points: ["Traceable reference materials", "EP/USP compliance documentation", "Periodic drift verification"],
  iconName: "scale",
  linkPath: "/QualificationLifecycleServicesPage"
}, {
  id: "modular-testing",
  title: "Modular Testing Adaptations",
  tag: "Custom Integration",
  description: "Customized flow cells, fiber-optic probes, and PAT integration accessories engineered to match specific vessel geometry and process lines.",
  points: ["Process line flow cells", "Custom probe mountings", "Scalable inline/at-line interfaces"],
  iconName: "sliders",
  linkPath: "/ProcessAnalyticalTechnologyPage"
}];
export default function ApplicationAreas() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (name: string) => {
    switch (name) {
      case "wrench":
        return <Wrench className="size-6 text-primary" />;
      case "tubes":
        return <TestTubes className="size-6 text-primary" />;
      case "scale":
        return <Scale className="size-6 text-primary" />;
      case "sliders":
      default:
        return <Sliders className="size-6 text-primary" />;
    }
  };
  return <section id="applicationareas" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider">
            Technical Capabilities
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-6">
            Core Application Areas
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Application-led analytical technology, qualification, and lifecycle support tailored for regulated laboratories and manufacturing teams.
          </p>
        </div>

        {/* 4 Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {applicationAreas.map((area, index) => <motion.div key={area.id} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-200">
                <div>
                  <CardHeader className="flex flex-row items-start justify-between gap-4 pb-4">
                    <div className="size-12 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                      {getIcon(area.iconName)}
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {area.tag}
                    </Badge>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <CardTitle className="text-xl sm:text-2xl font-default font-bold text-card-foreground">
                      {area.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      {area.description}
                    </CardDescription>

                    <div className="pt-3 border-t border-border/60">
                      <ul className="space-y-2">
                        {area.points.map((point, pointIndex) => <li key={pointIndex} data-index={pointIndex} className="flex items-center gap-2.5 text-xs sm:text-sm text-foreground/90">
                            <CheckCircle2 className="size-4 text-primary shrink-0" />
                            <span>{point}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-4 border-t border-border/40 mt-auto">
                  <Button asChild variant="ghost" size="sm" className="p-0 text-primary hover:text-primary hover:bg-transparent font-semibold">
                    <Link to={area.linkPath} className="inline-flex items-center gap-2">
                      Explore application details
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom CTA Banner */}
        <div className="mt-14 md:mt-18 p-8 md:p-10 rounded-2xl bg-secondary border border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="max-w-xl text-center md:text-left">
            <h3 className="text-xl sm:text-2xl font-default font-bold text-secondary-foreground mb-2">
              Require a tailored application setup?
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base">
              Speak directly with our analytical specialists across Ireland and the UK to discuss feasibility, trials, or on-site evaluations.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4 shrink-0">
            <Button asChild variant="default" size="lg">
              <Link to="/DiscussYourApplicationPage">
                Discuss Your Application
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/ContactPage">
                Contact Technical Support
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}